<template>
  <div class="record-page theme-bg min-h-screen">
    <!-- 顶部导航 -->
    <div class="fixed-header">
      <van-nav-bar 
        title="健康记录" 
        left-arrow 
        @click-left="onBack"
        class="theme-header"
        :border="false"
      />
    </div>
    
    <!-- 表单区域 -->
    <div class="pt-16 pb-20 px-4">
      <div class="bg-white rounded-2xl p-6 shadow-lg mb-6 theme-gradient-bg">
        <van-form @submit="onSubmit">
          <!-- 记录类型选择 -->
          <van-field 
            v-model="formData.healthRecordTypeText" 
            label="记录类型" 
            readonly 
            clickable 
            right-icon="arrow-down"
            placeholder="请选择记录类型"
            @click="showRecordTypePicker = true"
            required
            :rules="[{ required: true, message: '请选择记录类型' }]"
          />

          <!-- 通用字段 -->
          <van-field 
            v-model="formData.healthRecordName" 
            label="标题" 
            placeholder="输入记录标题" 
            required 
            :rules="[{ required: true, message: '请输入标题' }]"
          />
          <van-field 
            v-model="formData.healthRecordTime" 
            label="日期" 
            readonly 
            clickable 
            right-icon="calendar-o"
            placeholder="选择日期"
            @click="showDatePicker = true"
            required
            :rules="[{ required: true, message: '请选择日期' }]"
          />
          <van-field 
            v-model="formData.healthRecordCost" 
            label="花费(元)" 
            type="digit" 
            placeholder="0.00" 
            :formatter="value => value.replace(/^(\d+)(\.\d{0,2})?.*$/, '$1$2')"
          />
          <van-field 
            v-model="formData.hospitalName" 
            label="执行机构" 
            placeholder="宠物医院/诊所名称"
          />

          <!-- 疫苗记录特有字段 -->
          <div v-if="formData.healthRecordType === 1">
            <van-field 
              v-model="formData.vaccineName" 
              label="疫苗名称" 
              placeholder="输入疫苗名称"
            />
            <van-field 
              v-model="formData.vaccineBrand" 
              label="品牌" 
              placeholder="输入品牌名称"
            />
            <van-field 
              v-model="formData.vaccineBatchNumber" 
              label="批号" 
              placeholder="输入批号"
            />
            <van-field 
              v-model="formData.vaccineNextTime" 
              label="下次接种" 
              readonly 
              clickable 
              right-icon="calendar-o"
              placeholder="选择日期"
              @click="showNextDueDatePicker = true"
              required
              :rules="[{ required: true, message: '请选择下次接种日期' }]"
            />
          </div>

          <!-- 驱虫记录特有字段 -->
          <div v-if="formData.healthRecordType === 2">
            <van-field 
              v-model="formData.dewormingType" 
              label="驱虫类型" 
              placeholder="体内/体外驱虫"
            />
            <van-field 
              v-model="formData.drugName" 
              label="药品名称" 
              placeholder="输入药品名称"
            />
            <van-field 
              v-model="formData.dose" 
              label="剂量" 
              placeholder="输入使用剂量"
            />
            <van-field 
              v-model="formData.dewormingNextTime" 
              label="下次驱虫" 
              readonly 
              clickable 
              right-icon="calendar-o"
              placeholder="选择日期"
              @click="showNextDueDatePicker = true"
            />
          </div>

          <!-- 体检记录特有字段 -->
          <div v-if="formData.healthRecordType === 3">
            <van-field 
              v-model="formData.weight" 
              label="体重(kg)" 
              type="digit" 
              placeholder="输入体重"
            />
            <van-field 
              v-model="formData.temperature" 
              label="体温(℃)" 
              type="digit" 
              placeholder="输入体温"
            />
            <van-field 
              v-model="formData.heartRate" 
              label="心率(次/分钟)" 
              type="digit" 
              placeholder="输入心率"
            />
            <van-field 
              v-model="formData.inspectionResults" 
              label="检查结果" 
              type="textarea" 
              rows="2" 
              placeholder="输入检查结果"
            />
          </div>

          <!-- 生病记录特有字段 -->
          <div v-if="formData.healthRecordType === 4">
            <van-field 
              v-model="formData.symptom" 
              label="症状" 
              type="textarea" 
              rows="2" 
              placeholder="输入症状描述"
            />
            <van-field 
              v-model="formData.diagnosticResults" 
              label="诊断结果" 
              placeholder="输入诊断结果"
            />
            <van-field 
              v-model="formData.treatmentPlan" 
              label="治疗方案" 
              type="textarea" 
              rows="2" 
              placeholder="输入治疗方案"
            />
            <van-field 
              v-model="formData.medicationSituation" 
              label="用药情况" 
              type="textarea" 
              rows="2" 
              placeholder="输入用药情况"
            />
          </div>

          <!-- 附件上传 -->
          <van-field v-if="formData.healthRecordType === 4 || formData.healthRecordType === 3" name="uploader" label="上传附件">
            <template #input>
              <van-uploader 
                v-model="attachments" 
                :after-read="afterRead"
                multiple
                :max-count="5"
                :max-size="5 * 1024 * 1024"
                @oversize="onOversize"
              >
                <div class="upload-trigger bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 flex flex-col items-center justify-center">
                  <font-awesome-icon icon="plus-circle" class="text-2xl theme-icon" />
                  <div class="text-xs text-gray-500 mt-1">添加图片</div>
                </div>
              </van-uploader>
            </template>
          </van-field>

          <!-- 提交按钮 -->
          <div style="margin: 30px 16px 16px;">
            <van-button round block type="primary" native-type="submit" :loading="submitting" class="theme-btn-primary">保存记录</van-button>
          </div>
        </van-form>
      </div>
    </div>

    <!-- 日期选择器 -->
    <van-popup v-model="showDatePicker" position="bottom">
      <van-datetime-picker
        type="date"
        :min-date="new Date(2000, 0, 1)"
        :max-date="new Date()"
        :value="datePickerValue"
        @confirm="onDateConfirm"
        @cancel="showDatePicker = false"
      />
    </van-popup>

    <!-- 下次日期选择器 -->
    <van-popup v-model="showNextDueDatePicker" position="bottom">
      <van-datetime-picker
        type="date"
        :min-date="new Date()"
        :max-date="new Date(2050, 11, 31)"
        :value="nextDueDatePickerValue"
        @confirm="onNextDueDateConfirm"
        @cancel="showNextDueDatePicker = false"
      />
    </van-popup>
    
    <!-- 记录类型选择器 -->
    <van-popup v-model="showRecordTypePicker" position="bottom">
      <van-picker
        :columns="recordTypeOptions"
        @confirm="onRecordTypeConfirm"
        @cancel="showRecordTypePicker = false"
        show-toolbar
        title="选择记录类型"
      />
    </van-popup>
  </div>
</template>

<script>
// 暂时注释掉未使用的导入，直到真正使用时再取消注释
// import { healthRecordApi } from '@/api/config'

/**
 * 宠物健康记录组件
 * 支持添加疫苗、驱虫、体检、生病四种记录类型
 * @component
 */
import { RPC } from '../api/rpc'
import { mapActions } from 'vuex'
export default {
  name: 'HealthRecord',
  data() {
    return {
      /**
       * 表单数据
       * @type {Object}
       */
      formData: {
        "healthRecordTypeText": "",
        "dewormingNextTime": "",
        "dewormingType": "",
        "diagnosticImages": "",
        "diagnosticResults": "",
        "dose": "",
        "drugName": "",
        "healthRecordCost": 0,
        "healthRecordName": "",
        "healthRecordTime": "",
        "healthRecordType": 0,
        "heartRate": "",
        "hospitalName": "",
        "inspectionImages": "",
        "inspectionResults": "",
        "medicationSituation": "",
        "temperature": "",
        "treatmentPlan": "",
        "vaccineBatchNumber": "",
        "vaccineBrand": "",
        "vaccineName": "",
        "vaccineNextTime": "",
        "weight": ""
      },
      /**
       * 记录类型选项
       * @type {Array}
       */
      recordTypeOptions: [
        { text: '疫苗', value: 1 },
        { text: '驱虫', value: 2 },
        { text: '体检', value: 3 },
        { text: '生病', value: 4 }
      ],
      /**
       * 记录类型选择器显示状态
       * @type {Boolean}
       */
      showRecordTypePicker: false,
      /**
       * 日期选择器显示状态
       * @type {Boolean}
       */
      showDatePicker: false,
      /**
       * 下次日期选择器显示状态
       * @type {Boolean}
       */
      showNextDueDatePicker: false,
      /**
       * 日期选择器值
       * @type {Date}
       */
      datePickerValue: new Date(),
      /**
       * 下次日期选择器值
       * @type {Date}
       */
      nextDueDatePickerValue: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 默认30天后
      /**
       * 提交状态
       * @type {Boolean}
       */
      submitting: false,
      /**
       * 当前宠物ID
       * @type {String}
       */
      petId: '',
      /**
       * 上传状态
       * @type {Boolean}
       */
      uploading: false,
      detailData: {},
      attachments: [],
      addPetId: ''
    }
  },
  created() {
    // 获取路由参数中的宠物ID
    this.petId = this.$route.query.id || '';
    this.addPetId = this.$route.query.petId || '';
    if (this.petId) {
      RPC.healthRecordInfo(this.petId).then(res=>{
        this.formData = res.data
        this.attachments = res.data.diagnosticImages.split(',').map(url => ({ url: url.trim() }));
        this.formData.healthRecordTypeText = this.getRecordTypeTitle(res.data.healthRecordType)
      })
    }
    
  },
  methods: {
    ...mapActions(['setBackMachineId']),
    onBack() {
      this.setBackMachineId(this.addPetId)
      this.$router.go(-1)
    },
    getRecordTypeTitle(type) {
      const titles = {
        1: '疫苗',
        2: '驱虫',
        3: '体检',
        4: '生病'
      };
      return titles[type] || '记录信息';
    },
    /**
     * 处理记录类型选择确认
     * @param {Object} value - 选择的记录类型对象
     */
    onRecordTypeConfirm(value) {
      this.formData.healthRecordType = value.value;
      this.formData.healthRecordTypeText = value.text;
      this.showRecordTypePicker = false;
      
      // 根据记录类型重置特定字段
      if (value.value === 1) {
        // 保留通用字段，重置其他类型的特有字段
        this.resetSpecificFields([2,3,4]);
      } else if (value.value === 2) {
        this.resetSpecificFields([1,3,4]);
      } else if (value.value === 3) {
        this.resetSpecificFields([1,2,4]);
      } else if (value.value === 4) {
        this.resetSpecificFields([1,2,3]);
      }
    },
    /**
     * 重置特定类型字段
     * @param {Array} types - 需要重置的记录类型数组
     */
    resetSpecificFields(types) {
      const fieldsMap = {
        1: ['vaccine_name', 'brand', 'batch_number', 'next_due_date'],
        2: ['deworm_type', 'medicine_name', 'dosage', 'next_due_date'],
        3: ['weight', 'temperature', 'heart_rate', 'checkup_result'],
        4: ['symptoms', 'diagnosis', 'treatment', 'medications']
      };
      
      types.forEach(type => {
        fieldsMap[type].forEach(field => {
          this.formData[field] = '';
        });
      });
    },
    /**
     * 日期确认处理
     * @param {Date} value - 选择的日期
     */
    onDateConfirm(value) {
      // this.formData.date = this.formatDate(value);
      this.formData.healthRecordTime = this.formatDate(value);
      this.showDatePicker = false;
    },
    /**
     * 下次日期确认处理
     * @param {Date} value - 选择的日期
     */
    onNextDueDateConfirm(value) {
      // this.formData.next_due_date = this.formatDate(value);
      this.showNextDueDatePicker = false;
      this.formData.vaccineNextTime = this.formatDate(value);
      this.formData.dewormingNextTime = this.formatDate(value);
    },
    /**
     * 格式化日期
     * @param {Date} date - 日期对象
     * @returns {String} 格式化后的日期字符串 YYYY-MM-DD
     */
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    /**
     * 文件上传后处理
     * @param {Object} file - 上传的文件对象
     */
    async afterRead(file) {
      // 标记文件为上传中状态
      file.status = 'uploading';
      file.message = '上传中...';
      this.uploading = true;
      try {
        const client = new FormData();
        const files = file.file;
        client.append('file', files)
        RPC.uploadPetHealthImageFile(client).then(res=>{
          console.log('2345',res)
          file.status = 'done';
          file.message = '上传成功';
          file.url = res
        })
      } catch (error) {
        // 上传失败处理
        file.status = 'failed';
        file.message = '上传失败';
        console.error('文件上传失败:', error);
        this.$toast.fail('文件上传失败');
      } finally {
        this.uploading = false;
      }
    },
    /**
     * 文件大小超出限制处理
     */
    onOversize() {
      this.$toast('文件大小不能超过5MB');
    },
    /**
     * 表单提交处理
     */
    async onSubmit() {
      // 如果还有文件正在上传，提示用户等待
      if (this.uploading) {
        this.$toast('请等待文件上传完成');
        return;
      }
      // formData.healthRecordType === 3
      if(this.formData.healthRecordType === 3){
        this.formData.inspectionImages = this.attachments.filter(item => item.url).map(item => item.url).join(',')
      } else if(this.formData.healthRecordType === 4){
        this.formData.diagnosticImages = this.attachments.filter(item => item.url).map(item => item.url).join(',')
      }
      this.submitting = true;
      if (!this.petId) {
        this.formData.machineId = this.addPetId?this.addPetId:sessionStorage.getItem('deviceCode')?sessionStorage.getItem('deviceCode') : null
      }
      
      try {
        if (this.petId) {
          RPC.healthRecordUpdate(this.formData).then(res=>{
            console.log('res',res)
            this.$toast.success('记录保存成功');
            // 保存成功后返回上一页
            this.$router.back();
          })
        } else {
          RPC.healthRecordAdd(this.formData).then(res=>{
            console.log('res',res)
            this.setBackMachineId(this.formData.machineId)
            this.$toast.success('记录保存成功');
            // 保存成功后返回上一页
            this.$router.back();
          })
        }
        
      } catch (error) {
        console.error('保存记录失败:', error);
        this.$toast.fail('保存失败，请重试');
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.theme-header {
  background-image: var(--gradient-header);
  width: 100%;
  z-index: 50;
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

:deep(.van-button--primary) {
  background: linear-gradient(to right, var(--primary-color), var(--primary-light));
  border: none;
}

:deep(.van-nav-bar .van-icon) {
  color: var(--text-primary);
}

:deep(.van-nav-bar__title) {
  color: var(--text-primary);
  font-weight: 600;
}

.upload-trigger {
  width: 100px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.upload-trigger:hover {
  transform: scale(1.05);
}

:deep(.van-field__label) {
  color: var(--text-primary);
  font-weight: 500;
}
</style> 